/**
 * AssignedActions — persisted, manageable tracker for assigned Corvana AI tasks.
 * Overdue (carried-over) items surface first and stay until checked off. Crew can
 * tick them done or reopen; everything persists across reloads and days.
 */
import React, { useMemo } from 'react';
import { CheckCircle2, Circle, AlertTriangle, Clock, User, RotateCcw, ClipboardList } from 'lucide-react';
import { isOverdue, relativeDue } from './qsrTasks';

const priorityDot = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-slate-400' };

export default function AssignedActions({ tasks, onToggle }) {
  const { overdue, open, done } = useMemo(() => {
    const overdue = [], open = [], done = [];
    tasks.forEach((t) => {
      if (t.status === 'done') done.push(t);
      else if (isOverdue(t)) overdue.push(t);
      else open.push(t);
    });
    const byDue = (a, b) => new Date(a.due) - new Date(b.due);
    return { overdue: overdue.sort(byDue), open: open.sort(byDue), done };
  }, [tasks]);

  const Row = ({ t }) => {
    const rel = relativeDue(t.due);
    const isDone = t.status === 'done';
    return (
      <div className="flex items-start gap-3 px-4 py-3" data-testid={`assigned-task-${t.id}`}>
        <button onClick={() => onToggle(t.id)} className="mt-0.5 shrink-0 text-slate-400 hover:text-emerald-600 transition-colors" aria-label={isDone ? 'Reopen' : 'Mark done'} data-testid={`assigned-toggle-${t.id}`}>
          {isDone ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${isDone ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{t.title}</div>
          <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${priorityDot[t.priority] || priorityDot.medium}`} />{t.store}</span>
            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{t.owner}</span>
            <span className={`inline-flex items-center gap-1 ${rel.overdue ? 'text-rose-600 font-semibold' : ''}`}>
              {rel.overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{rel.label}
            </span>
          </div>
        </div>
        {isDone && <button onClick={() => onToggle(t.id)} className="shrink-0 text-slate-300 hover:text-slate-500" aria-label="Reopen"><RotateCcw className="w-4 h-4" /></button>}
      </div>
    );
  };

  return (
    <div className="rounded-3xl qsr-card shadow-sm" data-testid="assigned-actions">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-blue-50 border border-blue-200"><ClipboardList className="w-5 h-5 text-blue-600" /></div>
          <div>
            <h3 className="font-semibold text-base tracking-tight text-slate-900">Assigned actions</h3>
            <p className="text-xs text-slate-500">Carries over each day · stays until actioned</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-semibold">
          {overdue.length > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid="assigned-overdue-count">{overdue.length} overdue</span>}
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="assigned-open-count">{open.length} open</span>
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{done.length} done</span>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="px-4 py-6 text-sm text-slate-500 text-center">No assigned actions yet — assign one from the playbook above.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {overdue.length > 0 && (
            <div className="bg-rose-50/40">
              <div className="px-4 pt-2 text-[10px] font-bold uppercase tracking-wider text-rose-600">Overdue · action needed</div>
              {overdue.map((t) => <Row key={t.id} t={t} />)}
            </div>
          )}
          {open.map((t) => <Row key={t.id} t={t} />)}
          {done.length > 0 && (
            <div className="bg-slate-50/60">
              <div className="px-4 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Completed</div>
              {done.map((t) => <Row key={t.id} t={t} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
